import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';

import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { adminApi, type AdminSubscriptionRow } from '@/services/adminApi';
import { useAdminExportCsv } from '@/hooks/admin/useAdminExportCsv';
import { useAdminCursorList } from '@/hooks/admin/useAdminCursorList';
import { AdminListHeader, ActiveFiltersBar } from '@/components/admin/AdminListToolbar';
import { AdminListFooter } from '@/components/admin/AdminListFooter';
import { SortableTableHeader, type SortDir } from '@/components/admin/SortableTableHeader';

/**
 * AdminSubscriptionsPage — cross-tenant view of every active company's
 * billing snapshot. Reads from Firestore-cached billing data; refresh
 * button re-fetches without hitting Autumn.
 *
 * Filters: status, plan, segment, trial-ending-soon (7d). Inline
 * search filters loaded rows by name/email/plan.
 */
export default function AdminSubscriptionsPage() {
  const [status, setStatus] = useState('all');
  const [plan, setPlan] = useState('all');
  const [segment, setSegment] = useState('all');
  const [endingSoon, setEndingSoon] = useState(false);
  const [searchInline, setSearchInline] = useState('');

  const [sortBy, setSortBy] = useState<string | null>(null);
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const onSortChange = (field: string | null, dir: SortDir) => { setSortBy(field); setSortDir(dir); };

  const filterParams = useMemo(() => ({
    ...(status !== 'all' ? { status } : {}),
    ...(plan !== 'all' ? { plan } : {}),
    ...(segment !== 'all' ? { segment } : {}),
    ...(endingSoon ? { endingSoon: true } : {}),
    ...(sortBy ? { sortBy, sortDir } : {}),
  }), [status, plan, segment, endingSoon, sortBy, sortDir]);

  const { exportCsv, isExporting } = useAdminExportCsv('subscriptions', filterParams);

  const fetchPage = useCallback(
    (cursor: string | null) => adminApi.subscriptions.list({ ...filterParams, cursor }),
    [filterParams],
  );
  const {
    items, totalCount, loading, isRefreshing, error, refresh,
    hasMore, loadingMore, loadMore,
  } = useAdminCursorList<AdminSubscriptionRow>(fetchPage, [filterParams]);

  const filtered = useMemo(() => {
    if (!searchInline) return items;
    const s = searchInline.toLowerCase();
    return items.filter((r) =>
      (r.publicName || r.name).toLowerCase().includes(s)
      || (r.ownerEmail ?? '').toLowerCase().includes(s)
      || (r.planId ?? '').toLowerCase().includes(s)
      || r.companyId.toLowerCase().includes(s),
    );
  }, [items, searchInline]);

  const activeFilters: Array<{ key: string; label: string; onClear: () => void }> = [];
  if (status !== 'all') activeFilters.push({ key: 'status', label: `Status: ${status}`, onClear: () => setStatus('all') });
  if (plan !== 'all') activeFilters.push({ key: 'plan', label: `Plan: ${plan}`, onClear: () => setPlan('all') });
  if (segment !== 'all') activeFilters.push({ key: 'segment', label: `Segment: ${segment}`, onClear: () => setSegment('all') });
  if (endingSoon) activeFilters.push({ key: 'ending', label: 'Ending in 7d', onClear: () => setEndingSoon(false) });
  if (searchInline) activeFilters.push({ key: 'search', label: `Search: "${searchInline}"`, onClear: () => setSearchInline('') });

  const clearAll = () => {
    setStatus('all'); setPlan('all'); setSegment('all'); setEndingSoon(false); setSearchInline('');
  };

  return (
    <div className="space-y-4 max-w-6xl">
      <AdminListHeader
        title="Subscriptions"
        onRefresh={refresh}
        isRefreshing={isRefreshing}
        onExportCsv={exportCsv}
        isExporting={isExporting}
      />

      <div className="flex flex-wrap items-center gap-2">
        <Input
          placeholder="Search loaded rows (name, email, plan)..."
          value={searchInline}
          onChange={(e) => setSearchInline(e.target.value)}
          className="h-9 w-[260px]"
        />
        <Select value={status} onValueChange={setStatus}>
          <SelectTrigger className="h-9 w-[140px]"><SelectValue placeholder="Status" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Any status</SelectItem>
            <SelectItem value="trial">Trial</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="expired">Expired</SelectItem>
            <SelectItem value="none">None</SelectItem>
          </SelectContent>
        </Select>
        <Select value={plan} onValueChange={setPlan}>
          <SelectTrigger className="h-9 w-[160px]"><SelectValue placeholder="Plan" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All plans</SelectItem>
            <SelectItem value="freemium_trial">Freemium trial</SelectItem>
            <SelectItem value="starter">Starter</SelectItem>
            <SelectItem value="growth">Growth</SelectItem>
            <SelectItem value="professional">Professional</SelectItem>
            <SelectItem value="agency_starter">Agency Starter</SelectItem>
            <SelectItem value="agency_professional">Agency Pro</SelectItem>
            <SelectItem value="unlimited">Unlimited</SelectItem>
          </SelectContent>
        </Select>
        <Select value={segment} onValueChange={setSegment}>
          <SelectTrigger className="h-9 w-[130px]"><SelectValue placeholder="Segment" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All segments</SelectItem>
            <SelectItem value="sme">SME</SelectItem>
            <SelectItem value="agency">Agency</SelectItem>
            <SelectItem value="enterprise">Enterprise</SelectItem>
          </SelectContent>
        </Select>
        <div className="flex items-center gap-2 ml-1">
          <Switch id="ending-soon" checked={endingSoon} onCheckedChange={setEndingSoon} />
          <Label htmlFor="ending-soon" className="text-sm text-muted-foreground cursor-pointer">
            Trial ending in 7 days
          </Label>
        </div>
      </div>

      <ActiveFiltersBar filters={activeFilters} onClearAll={clearAll} />

      {error && <p className="text-sm text-destructive">{error}</p>}

      <Card className="border-border/50">
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border/50">
                  <th className="text-left py-3 px-4">
                    <SortableTableHeader label="Company" field="name" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} defaultDir="asc" />
                  </th>
                  <th className="text-left py-3 px-3">
                    <SortableTableHeader label="Segment" field="segment" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} defaultDir="asc" />
                  </th>
                  <th className="text-left font-medium text-muted-foreground py-3 px-3">Plan</th>
                  <th className="text-left font-medium text-muted-foreground py-3 px-3">Status</th>
                  <th className="text-left font-medium text-muted-foreground py-3 px-3">Trial ends</th>
                  <th className="text-left py-3 px-3">
                    <SortableTableHeader label="Plan updated" field="updatedAt" sortBy={sortBy} sortDir={sortDir} onChange={onSortChange} />
                  </th>
                </tr>
              </thead>
              <tbody>
                {loading && <tr><td colSpan={6} className="py-12 text-center text-muted-foreground">Loading...</td></tr>}
                {!loading && filtered.length === 0 && (
                  <tr><td colSpan={6} className="py-12 text-center text-muted-foreground">No subscriptions match.</td></tr>
                )}
                {filtered.map((row) => (
                  <tr key={row.companyId} className="border-b border-border/30 last:border-0 hover:bg-accent/40">
                    <td className="py-3 px-4">
                      <Link to={`/admin/customers/${row.companyId}`} className="font-medium hover:underline">
                        {row.publicName || row.name}
                      </Link>
                      <p
                        className="text-xs text-muted-foreground truncate max-w-[280px]"
                        title={row.ownerEmail ? `${row.ownerEmail} · ${row.companyId}` : row.companyId}
                      >
                        {row.ownerEmail ?? row.companyId}
                      </p>
                    </td>
                    <td className="py-3 px-3 text-muted-foreground capitalize">{row.segment}</td>
                    <td className="py-3 px-3 text-muted-foreground">{row.planId ?? '—'}</td>
                    <td className="py-3 px-3"><StatusBadge status={row.derivedStatus} /></td>
                    <td className="py-3 px-3 text-muted-foreground text-xs">
                      {row.trialEndsAt ? new Date(row.trialEndsAt).toLocaleDateString('en-GB') : '—'}
                    </td>
                    <td className="py-3 px-3 text-muted-foreground text-xs">
                      {row.planUpdatedAt ? new Date(row.planUpdatedAt).toLocaleDateString('en-GB') : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <AdminListFooter
            loadedCount={items.length}
            filteredCount={searchInline ? filtered.length : undefined}
            totalCount={totalCount}
            entityLabel="abbonamento"
            entityLabelPlural="abbonamenti"
            hasMore={hasMore}
            loadingMore={loadingMore}
            onLoadMore={loadMore}
          />
        </CardContent>
      </Card>
      {isRefreshing && <p className="text-xs text-muted-foreground text-right">Aggiornamento...</p>}
    </div>
  );
}

function StatusBadge({ status }: { status: 'trial' | 'active' | 'expired' | 'none' }) {
  const styles: Record<string, string> = {
    trial: 'bg-amber-100 text-amber-900',
    active: 'bg-success/15 text-success',
    expired: 'bg-destructive/10 text-destructive',
    none: 'bg-muted text-muted-foreground',
  };
  return <span className={`text-xs px-2 py-0.5 rounded-md capitalize ${styles[status]}`}>{status}</span>;
}
